import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Avatar,
  Box,
  Button,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import EditIcon from '@mui/icons-material/Edit';
import PublicIcon from '@mui/icons-material/Public';
import ScaledIngredientsList from 'Components/ScaledIngredientsList/ScaledIngredientsList';
import ShareButton from 'Components/ShareButton/ShareButton';
import ScalingControls from 'Components/ScalingControls/ScalingControls';
import { useAuth } from 'Hooks/useAuth';
import { useNotification } from 'Hooks/useNotification';
import ButtonAppBar from 'Layouts/ButtonAppBar/ButtonAppBar';
import { recipeService } from 'Services/recipeService';
import { userService } from 'Services/userService';
import { Ingredient, PublicUserProfile, Recipe } from 'Types/api';
import { scaleByIngredient, scaleByServings } from 'Utils/amountUtils';
import { downloadAsJson, downloadAsPdf } from 'Utils/exportUtils';
import { normalizeInstructions } from 'Utils/instructionUtils';
import { ROUTES } from 'Utils/routes';

const RecipeDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { userId, isAuthenticated, isLoading: authLoading } = useAuth();
  const { showNotification } = useNotification();
  const [recipe, setRecipe] = useState<Recipe | null>(null);
  const [author, setAuthor] = useState<PublicUserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [exportMenuAnchor, setExportMenuAnchor] = useState<null | HTMLElement>(null);
  const deletingRef = useRef(false);
  const fetchedRef = useRef(false);

  // Scaling state
  const [scaledServings, setScaledServings] = useState<number>(0);
  const [scaledIngredients, setScaledIngredients] = useState<Ingredient[]>([]);

  useEffect(() => {
    // Wait for auth to finish loading before fetching
    if (authLoading || !id || fetchedRef.current) return;

    fetchedRef.current = true;

    const fetchRecipe = async () => {
      try {
        // Use authenticated endpoint if logged in (returns own drafts + any published)
        // Use public endpoint if not logged in (returns published only)
        const data = isAuthenticated
          ? await recipeService.get(id)
          : await recipeService.getPublicById(id);
        setRecipe(data);
      } catch {
        setError('Recipe not found.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchRecipe();
  }, [id, isAuthenticated, authLoading]);

  // Initialize scaling when recipe loads
  useEffect(() => {
    if (recipe) {
      setScaledServings(recipe.servings);
      setScaledIngredients(recipe.ingredients);
    }
  }, [recipe]);

  // Fetch author profile for published recipes
  useEffect(() => {
    if (recipe?.status === 'published' && recipe.createdBy) {
      userService
        .getPublicProfile(recipe.createdBy)
        .then(setAuthor)
        .catch(() => void 0);
    }
  }, [recipe]);

  const handleServingsChange = (newServings: number) => {
    if (!recipe) return;
    const scaled = scaleByServings(recipe.servings, newServings, recipe.ingredients);
    setScaledServings(scaled.servings);
    setScaledIngredients(scaled.ingredients);
  };

  const handleIngredientAmountChange = (index: number, newAmount: number) => {
    if (!recipe) return;
    const scaled = scaleByIngredient(recipe.servings, recipe.ingredients, index, newAmount);
    setScaledServings(scaled.servings);
    setScaledIngredients(scaled.ingredients);
  };

  const handleResetScaling = () => {
    if (!recipe) return;
    setScaledServings(recipe.servings);
    setScaledIngredients(recipe.ingredients);
  };

  const isScaled = recipe !== null && scaledServings !== recipe.servings;

  const handleExportMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setExportMenuAnchor(event.currentTarget);
  };

  const handleExportMenuClose = () => {
    setExportMenuAnchor(null);
  };

  const handleExportJson = () => {
    if (recipe) {
      downloadAsJson(recipe);
    }
    handleExportMenuClose();
  };

  const handleExportPdf = async () => {
    if (recipe) {
      await downloadAsPdf(recipe);
    }
    handleExportMenuClose();
  };

  const handleDelete = async () => {
    if (!id || deletingRef.current) return;
    deletingRef.current = true;
    setIsDeleting(true);
    try {
      await recipeService.delete(id);
      showNotification('Recipe deleted successfully!', 'success');
      navigate(ROUTES.DASHBOARD);
    } catch {
      deletingRef.current = false;
      showNotification('Failed to delete recipe. Please try again.');
      setDeleteDialogOpen(false);
    } finally {
      setIsDeleting(false);
    }
  };

  const handlePublish = async () => {
    if (!id || !recipe || isPublishing) return;
    setIsPublishing(true);
    try {
      const updated = await recipeService.update(id, { status: 'published' });
      setRecipe(updated);
      showNotification('Recipe published successfully!', 'success');
    } catch {
      showNotification('Failed to publish recipe. Please try again.');
    } finally {
      setIsPublishing(false);
    }
  };

  if (authLoading || isLoading) {
    return (
      <>
        <ButtonAppBar />
        <Box sx={{ width: '100%' }}>
          <LinearProgress color='secondary' />
        </Box>
      </>
    );
  }

  if (error || !recipe) {
    return (
      <>
        <ButtonAppBar />
        <Container maxWidth='md'>
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Typography variant='h6' color='text.secondary' gutterBottom>
              {error || 'Recipe not found'}
            </Typography>
            <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(ROUTES.HOME)}>
              Back to Home
            </Button>
          </Box>
        </Container>
      </>
    );
  }

  const totalTime = recipe.prepTime + recipe.cookTime;
  const isOwner = isAuthenticated && !!userId && userId === recipe.createdBy;

  return (
    <>
      <ButtonAppBar />
      <Container maxWidth='md'>
        <Box component='main' sx={{ py: { xs: 2, sm: 3, md: 4 }, px: { xs: 1, sm: 2 } }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(isAuthenticated ? ROUTES.DASHBOARD : ROUTES.HOME)}
            sx={{ mb: 2 }}
          >
            {isAuthenticated ? 'Back to Recipes' : 'Back to Home'}
          </Button>

          <Box
            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}
          >
            <Typography variant='h4' component='h1'>
              {recipe.title}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              {recipe.status === 'published' && (
                <ShareButton
                  title={recipe.title}
                  description={recipe.description}
                  imageUrl={recipe.imageUrl}
                />
              )}
              <Button
                variant='outlined'
                startIcon={<DownloadIcon />}
                onClick={handleExportMenuOpen}
              >
                Export
              </Button>
              <Menu
                anchorEl={exportMenuAnchor}
                open={Boolean(exportMenuAnchor)}
                onClose={handleExportMenuClose}
              >
                <MenuItem onClick={handleExportJson}>Export as JSON</MenuItem>
                <MenuItem onClick={handleExportPdf}>Export as PDF</MenuItem>
              </Menu>
              {isOwner && (
                <>
                  {recipe.status === 'draft' && (
                    <Button
                      variant='contained'
                      color='success'
                      startIcon={<PublicIcon />}
                      onClick={handlePublish}
                      disabled={isPublishing}
                    >
                      {isPublishing ? 'Publishing...' : 'Publish'}
                    </Button>
                  )}
                  <Button
                    variant='outlined'
                    startIcon={<EditIcon />}
                    onClick={() => navigate(`/recipes/${id}/edit`)}
                  >
                    Edit
                  </Button>
                  <Button
                    variant='outlined'
                    color='error'
                    startIcon={<DeleteIcon />}
                    onClick={() => setDeleteDialogOpen(true)}
                  >
                    Delete
                  </Button>
                </>
              )}
            </Box>
          </Box>

          <Typography variant='body1' color='text.secondary' sx={{ mb: 2 }}>
            {recipe.description}
          </Typography>

          {recipe.status === 'published' && author && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Avatar src={author.avatarUrl} sx={{ width: 32, height: 32, fontSize: '0.875rem' }}>
                {author.displayName?.[0]?.toUpperCase() || '?'}
              </Avatar>
              <Typography variant='body2' color='text.secondary'>
                Recipe by <strong>{author.displayName || 'Anonymous Chef'}</strong>
              </Typography>
            </Box>
          )}

          {recipe.imageUrl && (
            <Box
              component='img'
              src={recipe.imageUrl}
              alt={recipe.title}
              sx={{
                width: '100%',
                maxHeight: 400,
                objectFit: 'cover',
                borderRadius: 2,
                mb: 3,
              }}
            />
          )}

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 3 }}>
            {recipe.status === 'draft' && <Chip label='Draft' color='warning' />}
            {recipe.status === 'published' && <Chip label='Published' color='success' />}
            <Chip
              icon={<AccessTimeIcon />}
              label={`Prep: ${recipe.prepTime} min`}
              variant='outlined'
            />
            <Chip
              icon={<AccessTimeIcon />}
              label={`Cook: ${recipe.cookTime} min`}
              variant='outlined'
            />
            <Chip icon={<AccessTimeIcon />} label={`Total: ${totalTime} min`} variant='outlined' />
            <Chip label={recipe.category} color='primary' variant='outlined' />
          </Box>

          <Divider sx={{ mb: 3 }} />

          <Typography variant='h5' component='h2' gutterBottom>
            Ingredients
          </Typography>

          <ScalingControls
            originalServings={recipe.servings}
            scaledServings={scaledServings}
            onServingsChange={handleServingsChange}
            onReset={handleResetScaling}
            isScaled={isScaled}
          />

          <ScaledIngredientsList
            ingredients={scaledIngredients}
            originalIngredients={recipe.ingredients}
            onIngredientAmountChange={handleIngredientAmountChange}
            isScaled={isScaled}
          />

          <Divider sx={{ my: 3 }} />

          <Typography variant='h5' component='h2' gutterBottom>
            Instructions
          </Typography>
          <List>
            {normalizeInstructions(recipe.instructions).map((instruction, index) => (
              <ListItem key={index} alignItems='flex-start' disablePadding sx={{ mb: 2 }}>
                <Box sx={{ width: '100%' }}>
                  <ListItemText primary={`${index + 1}. ${instruction.text}`} />
                  {instruction.imageUrl && (
                    <Box
                      component='img'
                      src={instruction.imageUrl}
                      alt={`Step ${index + 1}`}
                      sx={{
                        mt: 1,
                        maxWidth: '100%',
                        maxHeight: 300,
                        objectFit: 'cover',
                        borderRadius: 1,
                      }}
                    />
                  )}
                </Box>
              </ListItem>
            ))}
          </List>
        </Box>
      </Container>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Recipe</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete &quot;{recipe.title}&quot;? This action cannot be
            undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={isDeleting}>
            Cancel
          </Button>
          <Button onClick={handleDelete} color='error' disabled={isDeleting}>
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default RecipeDetail;
